<script>
export default {
	name: 'SavedListCard',
	inheritAttrs: false,
};
</script>

<script setup>
import OverlayBtmSticky from '~/components/OverlayBtmSticky.vue';
import EditUpdatePopup from './EditUpdatePopup.vue';
import DeleteConfirmationPopup from './DeleteConfirmationPopup.vue';
import AppHyperlink from '~/components/AppHyperlink.vue';
import Snackbar from '../AmalFavourite/Snackbar.vue';
import { onClickOutside } from '@vueuse/core';
import { isMobileViewport } from '~/logic/composables/breakpoints';



const props = defineProps({
	titleText: { type: String, default: '' },
	placesCountText: { type: String, default: '' },
	descriptionText: { type: String, default: '' },
	pagePath: { type: String, default: '' },
	ctaText: { type: String, default: '' },
	editNameText: { type: String, default: '' },
	removeNameText: { type: String, default: '' },
	openInNewTab: { type: Boolean, default: false },
	
	/* editListSection */
	editListTitle: { type: String, default: '' },
	listNamePlaceholderText: { type: String, default: '' },
	descriptionPlaceholderText: { type: String, default: '' },
	editCancelCtaLabel: { type: String, default: '' },
	editUpdateCtaLabel: { type: String, default: '' },
	duplicationMessage: { type: String, default: '' },
	fallbackErrorMessage: { type: String, default: '' },
	
	/* deleteListSection */
	deleteListTitle: { type: String, default: '' },
	deleteMessage: { type: String, default: '' },
	cancelCtaLabel: { type: String, default: '' },
	deleteCtaLabel: { type: String, default: '' },
	
	/* listUpdatedSnackBarSection */
	snackbarUpdatedMessage: { type: String, default: '' },
	
	/* listDeletedSnackBarSection */
	snackbarDeletedMessage: { type: String, default: '' },
	
	/* Card props config */
	savedListId: { type: [String, Number], default: null },
	savedListType: { type: String, default: 'halal-dining' },
	totalPlacesNumber: { type: Number, default: 0 },
	savedListUrlEndpoint: { type: String, default: '' },
	
});

const emit = defineEmits([
	'click-update-save-list',
	'click-delete-save-list',
]);

const isKebabMousePressed = ref(false);
const kebabMenuEl = ref(null);
const isKebabMenuOpen = ref(false);
const isKebabMobileMenuOpen = ref(false);
const editUpdateModelEl = ref(null);
const isEditUpdateModalOpen = ref(false);
const isDeleteModalOpen = ref(false);
const snackbarEl = ref(null);

onClickOutside(kebabMenuEl, () => {
	isKebabMenuOpen.value = false;
});

const kebabMenuButtonClick = () => {
	if (isMobileViewport.value) {
		isKebabMobileMenuOpen.value = true;
	} else {
		isKebabMenuOpen.value = true;
	}
};

const updatesavedListDetails = (values) => {
	emit('click-update-save-list', values);
	// isEditUpdateModalOpen.value = false;
	// snackbarEl.value.show({
	// 	message: props.snackbarUpdatedMessage,
	// 	isUndone: true,
	// });
};

const deleteSavedListHandler = () => {
	emit('click-delete-save-list');
	isDeleteModalOpen.value = false;
	// snackbarEl.value.show({
	// 	message: props.snackbarDeletedMessage,
	// 	isUndone: true,
	// });
};

const closeEditModal = () => {
	isEditUpdateModalOpen.value = false;
};

const closeDeleteModal = () => {
	isDeleteModalOpen.value = false;
};

const openDeleteModal = () => {
	if (isMobileViewport.value) {
		isKebabMobileMenuOpen.value = false;
	} else {
		isKebabMenuOpen.value = false;
	}
	isDeleteModalOpen.value = true;
};

defineExpose({
	closeDeleteModal,
	openDeleteModal,
	closeEditModal,
	editUpdateModelEl,
});
</script>


<template>
<div class="SavedListCard">
	<div class="flex items-center justify-between relative">
		<h5
			class="truncate mb-1"
			:title="props.titleText"
		>{{ props.titleText }}</h5>
		<button
			v-button-press
			type="button"
			class="kebab-menu-button"
			:class="{
				'isPressedDown': isKebabMousePressed,
				'kebabMenuOpen': isKebabMenuOpen,
			}"
			@mousedown="isKebabMousePressed = true"
			@keydown.enter.space="isKebabMousePressed = true"
			@mouseup="isKebabMousePressed = false"
			@keyup.enter.space="isKebabMousePressed = false"
			@mouseout="isKebabMousePressed = false"
			@click="kebabMenuButtonClick"
		>
			<icon-fas-ellipsis-vertical class="fill-primary-blue-base" />
		</button>
		<div
			ref="kebabMenuEl"
			class="kebab-menu more-buttons bg-neutral-white-base rounded-lg shadow-lg transition md:hidden"
			:class="{
				'hidden': !isKebabMenuOpen,
			}"
		>
			<button
				type="button"
				class="w-full px-4 py-2.5 flex gap-x-3 transition hover:bg-neutral-grey-extralight group"
				@click="isKebabMenuOpen = false; isEditUpdateModalOpen = true;"
			>
				<icon-fas-pen class="group-hover:(fill-primary-blue-base)" />
				<span class="group-hover:(text-primary-blue-base)">{{ props.editNameText }}</span>
			</button>
			<button
				type="button"
				class="w-full px-4 py-2.5 flex gap-x-3 transition hover:bg-neutral-grey-extralight group"
				@click="isKebabMenuOpen = false; isDeleteModalOpen = true;"
			>
				<icon-fas-trash-can class="group-hover:(fill-primary-blue-base)" />
				<span class="group-hover:(ftextll-primary-blue-base)">{{ props.removeNameText }}</span>
			</button>
		</div>
	</div>
	<div
		v-if="props.savedListType === 'halal-dining'"
		class="mt-2"
	>
		{{ $tc('{n} Dining Place', {
			n: props.totalPlacesNumber,
		}) }}
	</div>
	<div
		v-if="props.savedListType === 'place'"
		class="mt-2"
	>
		{{ $tc('{n} Place', {
			n: props.totalPlacesNumber,
		}) }}
	</div>
	<div
		class="line-clamp-2 mt-4 flex-1"
		:title="props.descriptionText"
	>{{ props.descriptionText }}</div>
	<AppHyperlink
		class="group mt-5"
		:href="props.pagePath"
		:target="(props.openInNewTab ? '_blank' : null)"
	>
		<span class="text-sm font-semibold">{{ props.ctaText }}</span>
		<icon-far-arrow-right
			class="text-sm fill-primary-blue-base ml-2 transition group-hover:(translate-x-1 rtl:-translate-x-1 transform)"
		/>
	</AppHyperlink>
</div>

<DeleteConfirmationPopup
	:isVisible="isDeleteModalOpen"
	:cancelCtaLabel="props.cancelCtaLabel"
	:deleteCtaLabel="props.deleteCtaLabel"
	:deleteMessageText="props.deleteMessage"
	:deleteListTitleText="props.deleteListTitle"
	@update:is-visible="isDeleteModalOpen = $event"
	@click-cancel="isDeleteModalOpen = false"
	@click-delete="deleteSavedListHandler"
/>

<EditUpdatePopup
	ref="editUpdateModelEl"
	:isVisible="isEditUpdateModalOpen"
	:editListTitleText="props.editListTitle"
	:editCancelCtaLabel="props.editCancelCtaLabel"
	:editUpdateCtaLabel="props.editUpdateCtaLabel"
	:savedTitle="props.titleText"
	:savedDescription="props.descriptionText"
	:listNamePlaceholderText="props.listNamePlaceholderText"
	:descriptionPlaceholderText="props.descriptionPlaceholderText"
	:duplicationMessage="props.duplicationMessage"
	:fallbackErrorMessage="props.fallbackErrorMessage"
	@update:is-visible="isEditUpdateModalOpen = $event"
	@click-cancel="isEditUpdateModalOpen = false"
	@click-update="updatesavedListDetails"
/>

<Snackbar ref="snackbarEl" />

<template v-if="isMobileViewport">
	<OverlayBtmSticky
		:isVisible="isKebabMobileMenuOpen"
		class="SavedListCardOverlay"
		@update:is-visible="isKebabMobileMenuOpen = $event"
	>
		<div class="px-4">
			<button
				type="button"
				class="w-full px-8 py-2.5 flex items-center gap-x-5 transition hover:bg-neutral-grey-extralight border-b border-b-neutral-grey-base group"
				@click="isKebabMobileMenuOpen = false; isEditUpdateModalOpen = true;"
			>
				<icon-fas-pen class="group-hover:(fill-primary-blue-base)" />
				<span class="text-sm group-hover:(text-primary-blue-base)">{{ props.editNameText }}</span>
			</button>
			<button
				type="button"
				class="w-full px-8 py-2.5 flex items-center gap-x-5 transition hover:bg-neutral-grey-extralight group"
				@click="isKebabMobileMenuOpen = false; isDeleteModalOpen = true;"
			>
				<icon-fas-trash-can class="group-hover:(fill-primary-blue-base)" />
				<span class="text-sm group-hover:(ftextll-primary-blue-base)">{{ props.removeNameText }}</span>
			</button>
		</div>
	</OverlayBtmSticky>
</template>
</template>

<style lang="scss">
.SavedListCardOverlay {
	.overlay-line {
		&::after {
			height: 6px;
			background-color: #EEF0F7;
		}
	}
	
	.overlay-container {
		min-height: 25vh;
	}
}
</style>


<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.SavedListCard {
	border-radius: 16px;
	border: 1px solid var(--secondary-teal-extralight);
	background: var(--secondary-teal-ultralight);
	padding: 24px;
	display: inline-flex;
	flex-direction: column;
	transition: box-shadow 250ms ease-in-out;
	
	&:hover {
		box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.12);
	}
	
	.kebab-menu-button {
		display: flex;
		justify-content: center;
		align-items: center;
		border-radius: 50%;
		padding: 4px;
		transition: all 150ms ease-in-out;
		border: 2px solid transparent;
		
		&:hover,
		&.kebabMenuOpen {
			background: #{color.adjust(var.$primary-blue-light, $alpha: -0.80)};
		}
		
		&.isPressedDown {
			background: #{color.adjust(var.$primary-blue-light, $alpha: -0.50)};
		}
	}
	
	.kebab-menu {
		position: absolute;
		right: -130px;
		top: 38px;
		left: auto;
	}
}


</style>